import { getNodesIntersectingRange, isText } from './dom/node';
import { cloneRangeWithPoints, createPointWithEndOfRange, createPointWithStartOfRange } from './dom/point';

export function toggleUppercaseInRange(container: Node, range: Range): Range {
  const startPoint = createPointWithStartOfRange(range);
  const endPoint = createPointWithEndOfRange(range);

  // Check if transform is already uppercase. If it is, then lowercase instead.
  let isAlreadyUppercase = true;
  for (const slice of visitNodesToTransform(container, range)) {
    isAlreadyUppercase &&= isTransformStable(slice, TextTransformsEnum.UPPERCASE);
  }

  const transform = isAlreadyUppercase ? TextTransformsEnum.LOWERCASE : TextTransformsEnum.UPPERCASE;

  // Apply transform.
  for (const slice of visitNodesToTransform(container, range)) {
    slice.text.data = getTransformedData(slice, transform);
  }

  return cloneRangeWithPoints(range, startPoint, endPoint);
}

interface TextSlice {
  text: Text;
  start: number;
  end?: number;
}

function* visitNodesToTransform(container: Node, range: Range): Iterable<TextSlice> {
  const { startContainer, startOffset, endContainer, endOffset } = range;

  for (const node of getNodesIntersectingRange(container, range, isText)) {
    if (node === startContainer || node === endContainer) {
      continue;
    }

    yield { text: node, start: 0 };
  }

  const areStartAndEndEqual = endContainer === startContainer;

  if (isText(endContainer) && areStartAndEndEqual) {
    yield { text: endContainer, start: startOffset, end: endOffset };
  }

  if (isText(endContainer) && !areStartAndEndEqual) {
    yield { text: endContainer, start: 0, end: endOffset };
  }

  if (isText(startContainer) && !areStartAndEndEqual) {
    yield { text: startContainer, start: startOffset };
  }
}

function isTransformStable(slice: TextSlice, transform: TextTransformsEnum) {
  const { text, start, end } = slice;
  const textToTransform = text.data.substring(start, end);

  return transformText(textToTransform, transform) === textToTransform;
}

function getTransformedData(slice: TextSlice, transform: TextTransformsEnum) {
  const { text, start, end } = slice;
  const before = text.data.substring(0, start);
  const textToTransform = text.data.substring(start, end);
  const after = end ? text.data.substring(end) : '';

  return before + transformText(textToTransform, transform) + after;
}

enum TextTransformsEnum {
  UPPERCASE = 'UPPERCASE',
  LOWERCASE = 'LOWERCASE',
}

function transformText(text: string, transform: TextTransformsEnum) {
  switch (transform) {
    case TextTransformsEnum.UPPERCASE:
      return text.toLocaleUpperCase();

    case TextTransformsEnum.LOWERCASE:
      return text.toLocaleLowerCase();

    default:
      return text;
  }
}
